import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { FormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TipoFirmanteCopiaBasica } from '../../../../envios/models/tipo-firmante';
import { FirmanteNif, TipoFirma } from '../../../models/firmante-nif';
import { FormularioDocumentoService } from '../../../services/formulario-documento.service';
import { MODO_ENVIO_SIMPLE } from '../../../services/modo-envio-simple.token';
import { resumenFirmaTarjeta } from '../../../services/resumen-firma-tarjeta';
import { firmanteColorCssVars } from '../firmante-color.tokens';
import { FirmaWizardInlineComponent } from '../firma-wizard-inline/firma-wizard-inline.component';
import { TarjetaAccionEstado } from '../tarjeta-accion-firmante/tarjeta-accion-firmante.component';

export type AccionFirmantePanel = 'firma' | 'formularios' | 'campos' | 'tipos-contenido' | null;

@Component({
  selector: 'app-panel-firmante-card',
  templateUrl: './panel-firmante-card.component.html',
  styleUrls: ['./panel-firmante-card.component.css'],
})
export class PanelFirmanteCardComponent implements OnInit, OnDestroy {
  @Input() firmanteUi: any;
  @Input() tipoFirmante: TipoFirmanteCopiaBasica;
  @Input() accionActiva: AccionFirmantePanel = null;
  @Input() esPrimerFirmante = false;
  @Input() tiposContenidoOverlayActivo = false;
  /** En Envio simple: mostrar accion para limpiar config solo en documento activo. */
  @Input() mostrarLimpiar = false;

  @Output() toggleExpand = new EventEmitter<FirmanteNif>();
  @Output() accionChange = new EventEmitter<AccionFirmantePanel>();
  @Output() firmanteSeleccionado = new EventEmitter<FirmanteNif>();
  @Output() tiposContenidoChange = new EventEmitter<boolean>();
  @Output() quitarFirmante = new EventEmitter<FirmanteNif>();
  @Output() limpiarEnDocumento = new EventEmitter<FirmanteNif>();

  @ViewChild('firmaWizard') firmaWizard?: FirmaWizardInlineComponent;

  colorVars: Record<string, string> = {};
  /** En Envio simple cambian las etiquetas y los adicionales muestran "x" para eliminarse. */
  readonly modoEnvioSimple: boolean;
  private tipoFirmaSub?: Subscription;
  private firmaPanelSub?: Subscription;

  constructor(
    public formularioDocumentoService: FormularioDocumentoService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MODO_ENVIO_SIMPLE) modoEnvioSimple: boolean
  ) {
    this.modoEnvioSimple = modoEnvioSimple ?? false;
  }

  ngOnInit(): void {
    this.colorVars = firmanteColorCssVars(this.tipoFirmante.codigo);
    this.watchTipoFirma();
    this.syncAccionConTipoFirma();
  }

  /** Etiqueta de la accion "Formularios" segun el modo (D2). */
  get etiquetaFormularios(): string {
    return this.modoEnvioSimple ? 'Casillas de verificación (formularios)' : 'Formularios';
  }

  /** Etiqueta de la accion "Campos editables" segun el modo (D2). */
  get etiquetaCampos(): string {
    return this.modoEnvioSimple ? 'Campo editable' : 'Campos editables';
  }

  /** El firmante principal nunca es eliminable (D5). */
  get puedeEliminarse(): boolean {
    return this.modoEnvioSimple && !this.esPrimerFirmante;
  }

  readonly tooltipLimpiarEnDocumento =
    'Limpia la configuración de este firmante solo en el documento activo';

  readonly tooltipQuitarFirmante =
    'Quita el firmante de todo el envío y elimina su configuración en todos los documentos';

  onQuitarClick(event: MouseEvent): void {
    event.stopPropagation();
    this.quitarFirmante.emit(this.firmanteUi);
  }

  onLimpiarClick(event: MouseEvent): void {
    event.stopPropagation();
    this.limpiarEnDocumento.emit(this.firmanteUi);
  }

  ngOnDestroy(): void {
    this.tipoFirmaSub?.unsubscribe();
    this.firmaPanelSub?.unsubscribe();
  }

  permiteFormulariosYCampos(): boolean {
    if (this.esRLT?.value) {
      return false;
    }
    const group = this.formularioDocumentoService.getFirmasGroup(this.tipoFirmante.codigo);
    return group?.get('tipoFirma')?.value !== TipoFirma.Validador;
  }

  get generico() {
    return this.formularioDocumentoService.formularioDocumento.controls['generico'];
  }

  get esRLT() {
    return this.formularioDocumentoService.formularioDocumento.controls['esRLT'];
  }

  get firmantesNif() {
    return this.formularioDocumentoService.formularioDocumento.controls['firmantesNif'] as FormArray;
  }

  onHeaderClick(): void {
    this.toggleExpand.emit(this.firmanteUi);
  }

  onAccionClick(accion: AccionFirmantePanel): void {
    if (accion === 'tipos-contenido') {
      this.firmanteSeleccionado.emit(this.firmanteUi);
      this.tiposContenidoChange.emit(true);
      return;
    }
    if ((accion === 'formularios' || accion === 'campos') && !this.permiteFormulariosYCampos()) {
      return;
    }
    this.firmanteSeleccionado.emit(this.firmanteUi);
    const next = this.accionActiva === accion ? null : accion;
    this.accionChange.emit(next);
    if (next === 'formularios') {
      this.onFormulariosInteract();
    }
  }

  get firmanteEmbed(): any {
    return { ...this.firmanteUi, seleccionado: true };
  }

  onFirmaConfig(): void {
    this.firmanteSeleccionado.emit(this.firmanteUi);
    this.accionChange.emit('firma');
  }

  estadoTarjeta(accion: AccionFirmantePanel): TarjetaAccionEstado {
    if (accion === 'tipos-contenido' && this.tiposContenidoOverlayActivo) {
      return 'activa';
    }
    if (this.accionActiva === accion) {
      return 'activa';
    }
    return 'idle';
  }

  firmaConfigurada(): boolean {
    return this.formularioDocumentoService.firmaCompletaParaFirmante(this.tipoFirmante.codigo);
  }

  getResumenFirma(): string {
    const wizard = this.firmaWizard;
    if (wizard && typeof wizard.getResumenFirma === 'function') {
      return wizard.getResumenFirma();
    }
    return this.resumenFirmaDesdeFormulario();
  }

  private resumenFirmaDesdeFormulario(): string {
    const group = this.formularioDocumentoService.getFirmasGroup(this.tipoFirmante.codigo);
    if (!group) {
      return '';
    }
    const posicionFirmas = group.get('posicionFirmas') as FormArray;
    return resumenFirmaTarjeta({
      tipo: group.get('tipoFirma')?.value,
      tieneTag: group.get('tieneTag')?.value,
      nombreTag: group.get('nombreTag')?.value,
      posicionFirmasCount: posicionFirmas?.length ?? 0,
    });
  }

  onFormulariosInteract(): void {
    if (!this.permiteFormulariosYCampos()) {
      return;
    }
    this.firmanteSeleccionado.emit(this.firmanteUi);
    if (!this.formularioDocumentoService.firmanteTieneBorradoresFormulario(this.tipoFirmante.codigo)) {
      this.formularioDocumentoService.addFormCasillas(this.firmanteUi);
    }
    this.cdr.detectChanges();
  }

  private watchTipoFirma(): void {
    const tipoCtrl = this.formularioDocumentoService.getFirmasGroup(this.tipoFirmante.codigo)?.get('tipoFirma');
    if (!tipoCtrl) {
      return;
    }
    this.tipoFirmaSub = tipoCtrl.valueChanges.subscribe(() => {
      this.syncAccionConTipoFirma();
      this.cdr.detectChanges();
    });
    this.firmaPanelSub = this.formularioDocumentoService.firmaPanelChanged().subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  private syncAccionConTipoFirma(): void {
    if (
      !this.permiteFormulariosYCampos() &&
      (this.accionActiva === 'formularios' || this.accionActiva === 'campos')
    ) {
      this.accionChange.emit(null);
    }
  }
}
